// library imports
import { Table, Flex, Button, Tooltip } from "antd";
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useEffect, useState, useContext } from "react";
import dayjs from "dayjs";
// style imports
import "../../styles/SavingReport/EnergySaving.css";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";

export default function SolarSaving({
  year,
  siteCommissioningDate,
  startDateOri,
  endDateOri,
  showPvSaving,
  reportModeSite,
  reportMode,
  committedReportMode,
}) {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  // startDate is the first day of the month based on left and right caret clicks
  const [startDate, setStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD"),
  );

  const [loading, setLoading] = useState(false);

  const [calculationValue, setCalculationValue] = useState(null);

  const columns = [
    {
      title: "",
      dataIndex: "ColumnName",
      width: "20%",
    },
    {
      title: "PV Self-Consumption",
      dataIndex: "PvSelco",
      width: "20%",
    },
    {
      title: (
        <Flex align="center">
          Excessive PV Solar
          <Tooltip title="BESS discharge energy sourced from excess PV">
            <Button type="text" icon={<InfoCircleOutlined />} />
          </Tooltip>
        </Flex>
      ),
      dataIndex: "ExcessivePvBess",
      width: "20%",
    },
    {
      title: "PV Export",
      dataIndex: "PvExport",
      width: "20%",
    },
    {
      title: (
        <Flex align="center">
          Saving
          {calculationValue && (
            <Tooltip
              title={
                <span style={{ whiteSpace: "pre-wrap" }}>
                  {calculationValue}
                </span>
              }
            >
              <Button type="text" icon={<InfoCircleOutlined />} />
            </Tooltip>
          )}
        </Flex>
      ),
      dataIndex: "Saving",
      width: "20%",
    },
  ];

  const [data, setData] = useState([]);

  /** API to get data related to PV saving */
  function getData() {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "saving_report/getSolarSavingData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: startDate,
      },

      onSuccess: (response) => {
        if (response.data === false) {
          notificationApi.error({
            message: response.message,
          });
        }
        setData(response.data.solar_saving_data);
        setCalculationValue(response.data.calculation_value);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get solar saving data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  /** Function to get month and year from startDate so that the chart title can be updated */
  const getSolarSavingTitle = () => {
    if (startDate) {
      // Create a new Date object from the startDate
      const dateObj = new Date(startDate);

      return dateObj.toLocaleString("en-US", {
        month: "long", // 'long' gives full month name (e.g., September)
        year: "numeric", // 'numeric' gives 4-digit year
      });
    } else {
      return "";
    }
  };

  /** Function to decrement the month when right caret is clicked */
  function decrementMonth() {
    // Create a Date object from startDate
    let dateObj = new Date(startDate);

    // Increment the month
    dateObj.setMonth(dateObj.getMonth() - 1);
    dateObj.setDate(1);

    // Format the new startDate back to 'YYYY-MM-DD'
    let newStartDate = dateObj.toISOString().split("T")[0]; // Splits to get 'YYYY-MM-DD'

    // Update the startDate to the new value
    setStartDate(newStartDate);
  }

  /** Function to increment the month when right caret is clicked */
  function incrementMonth() {
    // Create a Date object from startDate
    let dateObj = new Date(startDate);

    // Increment the month
    dateObj.setMonth(dateObj.getMonth() + 1);
    dateObj.setDate(1);

    // Format the new startDate back to 'YYYY-MM-DD'
    let newStartDate = dateObj.toISOString().split("T")[0]; // Splits to get 'YYYY-MM-DD'

    // Update the startDate to the new value
    setStartDate(newStartDate);
  }

  useEffect(() => {
    setData([]);
    if (
      startDate &&
      siteSelected &&
      showPvSaving &&
      reportModeSite === siteSelected
    ) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, siteSelected, showPvSaving, reportModeSite, committedReportMode]);

  useEffect(() => {
    setData([]);
  }, [reportMode]);

  useEffect(() => {
    if (year && siteCommissioningDate) {
      if (year.year() < dayjs().year()) {
        setStartDate(
          dayjs(
            Math.max(
              year.startOf("year").valueOf(),
              dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
            ),
          )
            .startOf("month")
            .format("YYYY-MM-DD"),
        );
      }
    } else {
      setStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
    }
  }, [year, siteCommissioningDate]);

  useEffect(() => {
    if (endDateOri) {
      setStartDate(dayjs(endDateOri).startOf("month").format("YYYY-MM-DD"));
    }
  }, [endDateOri]);

  return (
    <div className="energy-recharge">
      <Flex justify="space-between" align="center">
        <Button
          icon={
            parseInt(endDateOri.substring(5, 7)) ===
              parseInt(startDateOri.substring(5, 7)) ||
            parseInt(startDate.substring(5, 7)) === 1 ||
            parseInt(startDate.substring(5, 7)) ===
              parseInt(startDateOri.substring(5, 7)) ? (
              <></>
            ) : (
              <CaretLeftOutlined />
            )
          }
          type="text"
          disabled={loading}
          onClick={() => {
            decrementMonth();
          }}
        />
        <h1>Solar PV Generation Savings {getSolarSavingTitle()}</h1>
        <Button
          icon={
            parseInt(endDateOri.substring(5, 7)) ===
              parseInt(startDateOri.substring(5, 7)) ||
            parseInt(startDate.substring(5, 7)) === 12 ||
            parseInt(startDate.substring(5, 7)) ===
              parseInt(endDateOri.substring(5, 7)) ? (
              <></>
            ) : (
              <CaretRightOutlined />
            )
          }
          type="text"
          disabled={loading}
          onClick={() => {
            incrementMonth();
          }}
        />
      </Flex>

      <div>
        <Table
          loading={loading}
          style={{ width: "100%" }}
          columns={columns}
          dataSource={data}
          bordered
          size="small"
          pagination={{
            responsive: true,
            showSizeChanger: true,
            position: "bottomRight",
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>
    </div>
  );
}
